// src/services/assetImportService.js
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

import { assetService } from "./assetService";
import { categoryService } from "./categoryService";
import { subCategoryService } from "./subCategoryService";
import { brandService } from "./brandService";
import { vendorService } from "./vendorService";
import { locationService } from "./locationService";
import { departmentService } from "./departmentService";
import { userService } from "./userService";
import { customerService } from "./customerService";
import { singleValueListService } from "./singleValueListService";
import { preferencesService } from "./preferencesService";
import { generateQRToken } from "./qrTokenService";
import { repo } from "../dbservice/repo";
import { deriveApprovalStatus } from "../utils/approvalUtils";

import {
  getAssetTypeConfig,
  getOwnershipTypeConfig,
  resolveEffectiveSerialPolicy,
} from "../utils/assetUtils";

import {
  TEMPLATE_HEADERS,
  TEMPLATE_COLUMN_COUNT,
  HEADER_TO_FIELD,
  DROPDOWN_MAP,
  DROPDOWN_SOURCES,
} from "../utils/assetImportTemplate";
// Headers that are ALWAYS required on every row (mirrors validateRow's
// unconditional checks). Conditional ones (serial/Qty/UoM, lease/rent/sales
// blocks) are handled separately so we don't mislead users.
const ALWAYS_REQUIRED_HEADERS = Object.freeze(new Set([
  "Asset Name",
  "Asset Type",
  "Ownership Type",
  "Category Code",
  "Sub-Category Code",
  "Location Code",
  "Status",
  "Serialization Type",
  "Purchase Date",
]));

// Conditionally required — highlighted differently (amber italic) so users
// know to fill only when the Ownership/Asset Type matches.
const CONDITIONAL_HEADERS = Object.freeze(new Set([
  "Manufacturer Serial Number",
  "Quantity",
  "Unit of Measurement",
  "Purchase Value",
  "Vendor Name",
  "Lease Start Date",
  "Lease End Date",
  "Lease Amount",
  "Lease Vendor Name",
  "Lease Agreement Number",
  "Lease Billing Frequency",
  "Rent Start Date",
  "Expected Return Date",
  "Rent Amount",
  "Rent Vendor Name",
  "Rent Agreement Number",
  "Rent Billing Frequency",
  "Sales Date",
  "Sales Value",
  "Customer ID",
]));
// ── helpers ──────────────────────────────────────────────────
const generateUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const isEmpty = (v) => v == null || String(v).trim() === "";

const toNum = (v) => {
  if (isEmpty(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const isValidDate = (v) => {
  if (isEmpty(v)) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v).trim())) return false;
  const d = new Date(`${String(v).trim()}T00:00:00`);
  return !Number.isNaN(d.getTime());
};

const colLetter = (n) => {
  let s = "";
  let num = n;
  while (num > 0) {
    const m = (num - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    num = Math.floor((num - 1) / 26);
  }
  return s;
};

const catCodeOf = (c) =>
  String(c?.categoryCode || c?.categoryId || c?.code || "").trim();

const subCodeOf = (sc) =>
  String(sc?.subCategoryCode || sc?.subCategoryId || sc?.code || "").trim();
const serialKey = (v) => String(v ?? "").trim().toLowerCase();

// ── 1. Authenticity ──────────────────────────────────────────
function checkTemplateAuthenticity(worksheet) {
  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
  const headerRow = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c })];
    headerRow.push(cell ? String(cell.v || "").trim() : "");
  }

  // Trim trailing empty headers Excel sometimes adds
  while (headerRow.length > 0 && headerRow[headerRow.length - 1] === "") {
    headerRow.pop();
  }

  if (headerRow.length !== TEMPLATE_COLUMN_COUNT) {
    return {
      ok: false,
      message: `Invalid template: expected ${TEMPLATE_COLUMN_COUNT} columns, found ${headerRow.length}. Use the official Niyantrix template only.`,
    };
  }

  for (let i = 0; i < TEMPLATE_COLUMN_COUNT; i++) {
    if (headerRow[i] !== TEMPLATE_HEADERS[i]) {
      return {
        ok: false,
        message: `Header mismatch at column ${i + 1}: expected "${TEMPLATE_HEADERS[i]}", found "${headerRow[i]}". Do not change header names or order.`,
      };
    }
  }

  return { ok: true };
}

// All TEMPLATE_HEADERS-mapped fields that hold a calendar date.
// Kept here (not in assetImportTemplate.js) because it's a parsing concern,
// not a template-contract concern — the header list stays untouched.
const DATE_FIELDS = new Set([
  "purchaseDate",
  "warrantyExpiry",
  "leaseStartDate",
  "leaseEndDate",
  "rentStartDate",
  "expectedReturnDate",
  "salesDate",
  "maintenanceStartDate",
]);

// Excel hands back dates as either:
//   - a Date object (when raw:true)
//   - a formatted string like "2026-10-31 00:00:00" (when raw:false)
//   - a numeric serial (rare, depends on cell format)
// Normalize every variant to "YYYY-MM-DD" so isValidDate + payload keep working.
function normalizeDateCell(value) {
  if (value == null) return "";
  const str = String(value).trim();
  if (str === "") return "";

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  // "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss..." → take the date part
  const dateTimeMatch = str.match(/^(\d{4}-\d{2}-\d{2})[ T]/);
  if (dateTimeMatch) {
    const candidate = dateTimeMatch[1];
    const d = new Date(`${candidate}T00:00:00`);
    if (!Number.isNaN(d.getTime())) return candidate;
  }

  // "DD/MM/YYYY" or "MM/DD/YYYY" — best-effort; try Date parsing
  // (this handles locales where the template output was reformatted by Excel)
  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  // Unparseable — return as-is so validation reports a clean error
  return str;
}

function parseRows(worksheet) {
  const json = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false,
  });

  if (json.length < 2) {
    return { ok: false, message: "File has no data rows." };
  }

  const dataRows = [];
  for (let r = 1; r < json.length; r++) {
    const rowArr = json[r];
    if (!rowArr || rowArr.every((c) => String(c || "").trim() === "")) continue;

    const obj = {};
    TEMPLATE_HEADERS.forEach((h, i) => {
      const field = HEADER_TO_FIELD[h];
      const raw = rowArr[i];
      if (DATE_FIELDS.has(field)) {
        obj[field] = normalizeDateCell(raw);
      } else {
        obj[field] = raw != null ? String(raw).trim() : "";
      }
    });
    obj.__rowNumber = r + 1; // Excel row number (1-based, header is row 1)
    dataRows.push(obj);
  }

  if (dataRows.length === 0) {
    return { ok: false, message: "No non-empty data rows found." };
  }

  return { ok: true, rows: dataRows };
}
// ── 3. Load masters ──────────────────────────────────────────
async function loadMasters(companyId) {
  const [
    categories,
    subCategories,
    brands,
    vendors,
    locations,
    departments,
    users,
    customers,
    assetTypes,
    ownershipTypes,
    statuses,
    depMethods,
    uoms,
    maintIntervals,
      billingFreqs,
    approvers,
  ] = await Promise.all([
    categoryService.fetchCategories({ companyId }),
    subCategoryService.fetchSubCategories({ companyId }),
    brandService.fetchBrands({ companyId }),
    vendorService.fetchVendors({ companyId }),
    locationService.fetchLocations({ companyId }),
    departmentService.fetchDepartments({ companyId }),
    userService.fetchUsers(companyId),
    customerService.fetchCustomers({ companyId, status: "active" }),
    singleValueListService.getItems("asset_types", { companyId }),
    singleValueListService.getItems("ownership_types", { companyId }),
    singleValueListService.getItems("asset_statuses", { companyId }),
    singleValueListService.getItems("depreciation_methods", { companyId }),
    singleValueListService.getItems("units_of_measurement", { companyId }),
    singleValueListService.getItems("maintenance_intervals", { companyId }),
      singleValueListService.getItems("billing_frequencies", { companyId }),
    singleValueListService.getApproverItems(companyId),
  ]);
     return {
    categories: categories || [],
    subCategories: subCategories || [],
    brands: brands || [],
    vendors: vendors || [],
    locations: locations || [],
    departments: departments || [],
    users: users || [],
    customers: customers || [],
    assetTypes: assetTypes || [],
    ownershipTypes: ownershipTypes || [],
    statuses: statuses || [],
    depMethods: depMethods || [],
    uoms: uoms || [],
    maintIntervals: maintIntervals || [],
    billingFreqs: billingFreqs || [],
    approvers: approvers || [],
    ownershipEnabled: false, 
  };
}

// ── 4. Validate one row ──────────────────────────────────────
function validateRow(row, masters) {
  const errors = [];
  const {
    categories,
    subCategories,
    brands,
    vendors,
    locations,
    departments,
    users,
    customers,
    assetTypes,
    ownershipTypes,
    statuses,
    depMethods,
  } = masters;

  // Core always required
  if (isEmpty(row.assetName)) errors.push("Asset Name is required");
  if (isEmpty(row.assetType)) errors.push("Asset Type is required");
  if (isEmpty(row.ownershipType)) errors.push("Ownership Type is required");
  if (isEmpty(row.categoryCode)) errors.push("Category Code is required");
  if (isEmpty(row.subCategoryCode)) errors.push("Sub-Category Code is required");
  if (isEmpty(row.locationCode)) errors.push("Location Code is required");
  if (isEmpty(row.status)) errors.push("Status is required");
  if (isEmpty(row.serializationType)) errors.push("Serialization Type is required");
  if (
    !isEmpty(row.serializationType) &&
    !["serialized", "non-serialized"].includes(
      String(row.serializationType).toLowerCase()
    )
  ) {
    errors.push(`Serialization Type must be "serialized" or "non-serialized"`);
  }

  // SVL checks
  if (
    row.assetType &&
    !assetTypes.some(
      (t) => String(t.value).toLowerCase() === String(row.assetType).toLowerCase()
    )
  ) {
    errors.push(`Asset Type "${row.assetType}" not found`);
  }
  if (
    row.ownershipType &&
    !ownershipTypes.some(
      (t) =>
        String(t.value).toLowerCase() === String(row.ownershipType).toLowerCase()
    )
  ) {
    errors.push(`Ownership Type "${row.ownershipType}" not found`);
  }
  if (
    row.status &&
    !statuses.some(
      (t) => String(t.value).toLowerCase() === String(row.status).toLowerCase()
    )
  ) {
    errors.push(`Status "${row.status}" not found`);
  }

  // Category
  const cat = categories.find(
    (c) =>
      catCodeOf(c).toLowerCase() === String(row.categoryCode).toLowerCase() ||
      String(c.id || "").toLowerCase() === String(row.categoryCode).toLowerCase()
  );
  if (row.categoryCode && !cat) {
    errors.push(`Category Code "${row.categoryCode}" not found`);
  }

  // Sub-category under category
  let subCat = null;
  if (cat) {
    subCat = subCategories.find(
      (sc) =>
        (subCodeOf(sc).toLowerCase() === String(row.subCategoryCode).toLowerCase() ||
          String(sc.id || "").toLowerCase() ===
            String(row.subCategoryCode).toLowerCase()) &&
        String(sc.categoryId) === String(cat.id)
    );
    if (row.subCategoryCode && !subCat) {
      errors.push(
        `Sub-Category Code "${row.subCategoryCode}" not found under Category "${row.categoryCode}"`
      );
    }
  }

  // Location
  if (
    row.locationCode &&
    !locations.some(
      (l) =>
        String(l.locationCode || "").toLowerCase() ===
        String(row.locationCode).toLowerCase()
    )
  ) {
    errors.push(`Location Code "${row.locationCode}" not found`);
  }

  // Optional masters
  if (
    !isEmpty(row.brandName) &&
    !brands.some(
      (b) =>
        String(b.name || b.brandName || "").toLowerCase() ===
        String(row.brandName).toLowerCase()
    )
  ) {
    errors.push(`Brand "${row.brandName}" not found`);
  }
  if (
    !isEmpty(row.vendorName) &&
    !vendors.some(
      (v) =>
        String(v.vendorName || "").toLowerCase() ===
        String(row.vendorName).toLowerCase()
    )
  ) {
    errors.push(`Vendor "${row.vendorName}" not found`);
  }
  if (
    !isEmpty(row.assignedToEmail) &&
    !users.some(
      (u) =>
        String(u.email || "").toLowerCase() ===
        String(row.assignedToEmail).toLowerCase()
    )
  ) {
    errors.push(`Assigned To email "${row.assignedToEmail}" not found`);
  }
  if (
    !isEmpty(row.ownerEmail) &&
    !users.some(
      (u) =>
        String(u.email || "").toLowerCase() ===
        String(row.ownerEmail).toLowerCase()
    )
  ) {
    errors.push(`Asset Owner email "${row.ownerEmail}" not found`);
  }
  if (
    !isEmpty(row.assignedDepartmentName) &&
    !departments.some(
      (d) =>
        String(d.departmentName || d.name || "").toLowerCase() ===
        String(row.assignedDepartmentName).toLowerCase()
    )
  ) {
    errors.push(`Department "${row.assignedDepartmentName}" not found`);
  }

  // Serial policy (Rented/Leased always serialized)
  const ownLower = String(row.ownershipType || "").toLowerCase();
  const isReturnOnly = ownLower === "rented" || ownLower === "leased";
  const serialPolicy = isReturnOnly
    ? "required"
    : resolveEffectiveSerialPolicy(row.assetType, subCat);
  const isSerialized =
    serialPolicy === "required"
      ? true
      : serialPolicy === "disabled"
        ? false
        : String(row.serializationType).toLowerCase() === "serialized";

  if (isSerialized) {
    if (isEmpty(row.manufacturerSerialNumber)) {
      errors.push(
        "Manufacturer Serial Number is required for serialized assets"
      );
    }
  } else {
    const qty = toNum(row.quantity);
    if (qty === null || Number.isNaN(qty) || qty < 1) {
      errors.push("Quantity must be a number ≥ 1 for non-serialized assets");
    }
    if (isEmpty(row.unitOfMeasurement)) {
      errors.push("Unit of Measurement is required for non-serialized assets");
    }
  }

  // Parent when required by asset type
  const typeCfg = getAssetTypeConfig(row.assetType);
  if (typeCfg.requiresParent && isEmpty(row.parentAssetId)) {
    errors.push(
      `Parent Asset ID is required for Asset Type "${row.assetType}"`
    );
  }

  // Purchase Date is required on EVERY asset (regardless of ownership)
  if (isEmpty(row.purchaseDate)) {
    errors.push("Purchase Date is required for all assets");
  } else if (!isValidDate(row.purchaseDate)) {
    errors.push("Purchase Date must be YYYY-MM-DD");
  }

  // ── Ownership-specific requirements (mirrors CreateEditAssetModal) ──
  const ownershipCfg = getOwnershipTypeConfig(row.ownershipType);
  const requiresBillingFrequency = ownershipCfg.requiresBillingFrequency;

  const present = (v) => !isEmpty(v);

  const LEASE_FIELDS = [
    "leaseStartDate",
    "leaseEndDate",
    "leaseAmount",
    "leaseVendorName",
    "leaseAgreementNumber",
    "leaseBillingFrequency",
  ];
  const RENT_FIELDS = [
    "rentStartDate",
    "expectedReturnDate",
    "rentAmount",
    "rentVendorName",
    "rentAgreementNumber",
    "rentBillingFrequency",
  ];
  const SALES_FIELDS = ["salesDate", "salesValue", "customerId"];

  const rejectBlock = (label, fields) => {
    const filled = fields.filter((f) => present(row[f]));
    if (filled.length > 0) {
      errors.push(
        `${label} fields must be blank for ${row.ownershipType} assets (found: ${filled.join(", ")})`
      );
    }
  };

  const isCustomerAssetType =
    String(row.assetType || "").toLowerCase() === "customer";

  if (ownLower === "owned") {
    const pv = toNum(row.purchaseValue);
    if (pv === null || Number.isNaN(pv) || pv < 0) {
      errors.push("Purchase Value must be a number ≥ 0 for Owned assets");
    }
    if (isEmpty(row.vendorName)) {
      errors.push("Vendor Name is required for Owned assets");
    } else if (
      !vendors.some(
        (v) =>
          String(v.vendorName || "").toLowerCase() ===
          String(row.vendorName).toLowerCase()
      )
    ) {
      errors.push(`Vendor "${row.vendorName}" not found`);
    }
    rejectBlock("Lease", LEASE_FIELDS);
    rejectBlock("Rent", RENT_FIELDS);
  }

  if (ownLower === "leased") {
    if (isEmpty(row.leaseStartDate)) {
      errors.push("Lease Start Date is required for Leased assets");
    } else if (!isValidDate(row.leaseStartDate)) {
      errors.push("Lease Start Date must be YYYY-MM-DD");
    }
    if (isEmpty(row.leaseEndDate)) {
      errors.push("Lease End Date is required for Leased assets");
    } else if (!isValidDate(row.leaseEndDate)) {
      errors.push("Lease End Date must be YYYY-MM-DD");
    }
    if (
      !isEmpty(row.leaseStartDate) &&
      !isEmpty(row.leaseEndDate) &&
      isValidDate(row.leaseStartDate) &&
      isValidDate(row.leaseEndDate) &&
      new Date(row.leaseEndDate) <= new Date(row.leaseStartDate)
    ) {
      errors.push("Lease End Date must be after Lease Start Date");
    }
    if (isEmpty(row.leaseAmount) || Number.isNaN(toNum(row.leaseAmount))) {
      errors.push("Lease Amount is required for Leased assets");
    } else if (toNum(row.leaseAmount) <= 0) {
      errors.push("Lease Amount must be greater than zero");
    }
    if (isEmpty(row.leaseVendorName)) {
      errors.push("Lease Vendor Name is required for Leased assets");
    } else if (
      !vendors.some(
        (v) =>
          String(v.vendorName || "").toLowerCase() ===
          String(row.leaseVendorName).toLowerCase()
      )
    ) {
      errors.push(`Lease Vendor "${row.leaseVendorName}" not found`);
    }
    if (isEmpty(row.leaseAgreementNumber)) {
      errors.push("Lease Agreement Number is required for Leased assets");
    }
    if (requiresBillingFrequency && isEmpty(row.leaseBillingFrequency)) {
      errors.push("Lease Billing Frequency is required for Leased assets");
    }
    rejectBlock("Rent", RENT_FIELDS);
    rejectBlock("Sales", SALES_FIELDS);
  }

  if (ownLower === "rented") {
    if (isEmpty(row.rentStartDate)) {
      errors.push("Rent Start Date is required for Rented assets");
    } else if (!isValidDate(row.rentStartDate)) {
      errors.push("Rent Start Date must be YYYY-MM-DD");
    }
    if (isEmpty(row.expectedReturnDate)) {
      errors.push("Expected Return Date is required for Rented assets");
    } else if (!isValidDate(row.expectedReturnDate)) {
      errors.push("Expected Return Date must be YYYY-MM-DD");
    }
    if (
      !isEmpty(row.rentStartDate) &&
      !isEmpty(row.expectedReturnDate) &&
      isValidDate(row.rentStartDate) &&
      isValidDate(row.expectedReturnDate) &&
      new Date(row.expectedReturnDate) <= new Date(row.rentStartDate)
    ) {
      errors.push("Expected Return Date must be after Rent Start Date");
    }
    if (isEmpty(row.rentAmount) || Number.isNaN(toNum(row.rentAmount))) {
      errors.push("Rent Amount is required for Rented assets");
    } else if (toNum(row.rentAmount) <= 0) {
      errors.push("Rent Amount must be greater than zero");
    }
    if (isEmpty(row.rentVendorName)) {
      errors.push("Rent Vendor Name is required for Rented assets");
    } else if (
      !vendors.some(
        (v) =>
          String(v.vendorName || "").toLowerCase() ===
          String(row.rentVendorName).toLowerCase()
      )
    ) {
      errors.push(`Rent Vendor "${row.rentVendorName}" not found`);
    }
    if (isEmpty(row.rentAgreementNumber)) {
      errors.push("Rent Agreement Number is required for Rented assets");
    }
    if (requiresBillingFrequency && isEmpty(row.rentBillingFrequency)) {
      errors.push("Rent Billing Frequency is required for Rented assets");
    }
    rejectBlock("Lease", LEASE_FIELDS);
    rejectBlock("Sales", SALES_FIELDS);
  }

  if (isCustomerAssetType) {
    if (isEmpty(row.salesDate)) {
      errors.push("Sales Date is required for Customer assets");
    } else if (!isValidDate(row.salesDate)) {
      errors.push("Sales Date must be YYYY-MM-DD");
    }
    if (isEmpty(row.salesValue) || Number.isNaN(toNum(row.salesValue))) {
      errors.push("Sales Value is required for Customer assets");
    } else if (toNum(row.salesValue) <= 0) {
      errors.push("Sales Value must be greater than zero");
    }
    if (isEmpty(row.customerId)) {
      errors.push("Customer ID is required for Customer assets");
    } else if (
      !customers.some(
        (c) =>
          String(c.customerId || c.id || "").toLowerCase() ===
          String(row.customerId).toLowerCase()
      )
    ) {
      errors.push(`Customer ID "${row.customerId}" not found`);
    }
  } else {
    rejectBlock("Sales", SALES_FIELDS);
  }

  // Optional dates: if filled, must be valid
  [
    "warrantyExpiry",
    "leaseStartDate",
    "leaseEndDate",
    "rentStartDate",
    "expectedReturnDate",
    "salesDate",
    "maintenanceStartDate",
  ].forEach((f) => {
    if (!isEmpty(row[f]) && !isValidDate(row[f])) {
      errors.push(`${f} must be YYYY-MM-DD`);
    }
  });

  // Depreciation (optional)
  if (!isEmpty(row.depreciationMethod)) {
    const method = String(row.depreciationMethod).toUpperCase();
    if (!depMethods.some((d) => String(d.value).toUpperCase() === method)) {
      errors.push(`Depreciation Method "${row.depreciationMethod}" not found`);
    }
    if (method === "SLM" || method === "STRAIGHT-LINE") {
      const life = toNum(row.usefulLife);
      if (life === null || Number.isNaN(life) || life < 1 || life > 50) {
        errors.push("Useful Life must be 1–50 years for SLM");
      }
    }
    if (method === "WDV" || method === "WRITTEN-DOWN-VALUE") {
      const rate = toNum(row.depreciationRate);
      if (rate === null || Number.isNaN(rate) || rate < 0 || rate > 100) {
        errors.push("Depreciation Rate must be 0–100 for WDV");
      }
    }
  }

  return errors;
}

// ── 5. Validate ALL rows first (no inserts) ──────────────────
async function validateAllRows(rows, companyId, masters) {
  const existing = await assetService.fetchAssets({ companyId });
  const existingByAssetId = new Map(
    (existing || []).map((a) => [String(a.assetId || "").toLowerCase(), a])
  );

  // Index existing serials for duplicate detection
  const existingSerials = new Set(
    (existing || [])
      .map((a) => serialKey(a.manufacturerSerialNumber))
      .filter(Boolean)
  );

  // Track serials seen within this file
  const seenSerials = new Map(); // serial -> first row number

  const allErrors = [];

  for (const row of rows) {
    const messages = validateRow(row, masters);

    // ── Duplicate manufacturer serial (in file) ──
    const sn = serialKey(row.manufacturerSerialNumber);
    if (sn) {
      if (seenSerials.has(sn)) {
        messages.push(
          `Duplicate Manufacturer Serial Number "${row.manufacturerSerialNumber}" — already used at Row ${seenSerials.get(sn)}`
        );
      } else {
        seenSerials.set(sn, row.__rowNumber);
      }
      // ── Duplicate manufacturer serial (already in DB) ──
      if (existingSerials.has(sn)) {
        messages.push(
          `Manufacturer Serial Number "${row.manufacturerSerialNumber}" already exists in the database`
        );
      }
    }

    if (messages.length) {
      allErrors.push({ row: row.__rowNumber, messages });
    }
  }

  // Parent must exist in DB (business assetId). We resolve the parent's
  // DOC ID here so buildPayload can store it exactly like the modal does.
  for (const row of rows) {
    if (isEmpty(row.parentAssetId)) continue;
    const parentLower = String(row.parentAssetId).toLowerCase();
    const parentDoc = existingByAssetId.get(parentLower);
    if (!parentDoc) {
      const msg = `Parent Asset ID "${row.parentAssetId}" not found in database`;
      const existingErr = allErrors.find((e) => e.row === row.__rowNumber);
      if (existingErr) existingErr.messages.push(msg);
      else allErrors.push({ row: row.__rowNumber, messages: [msg] });
    } else {
      row.__parentDocId = parentDoc.id;
      row.__parentAssetName = parentDoc.assetName || null;
    }
  }

  return allErrors;
}
// ── 6. Build payload ─────────────────────────────────────────
function buildPayload(
  row,
  masters,
  currentUser,
  targetDocId,
  aId,
  qrToken,
  ownershipEnabled,
  approverEnabled
) {
  const {
    categories,
    subCategories,
    brands,
    vendors,
    departments,
    users,
    customers,
  } = masters;

  const cat = categories.find(
    (c) =>
      catCodeOf(c).toLowerCase() === String(row.categoryCode).toLowerCase() ||
      String(c.id || "").toLowerCase() === String(row.categoryCode).toLowerCase()
  );
  const subCat = subCategories.find(
    (sc) =>
      (subCodeOf(sc).toLowerCase() === String(row.subCategoryCode).toLowerCase() ||
        String(sc.id || "").toLowerCase() ===
          String(row.subCategoryCode).toLowerCase()) &&
      String(sc.categoryId) === String(cat?.id)
  );
  const brand = brands.find(
    (b) =>
      String(b.name || b.brandName || "").toLowerCase() ===
      String(row.brandName || "").toLowerCase()
  );
  const vendor = vendors.find(
    (v) =>
      String(v.vendorName || "").toLowerCase() ===
      String(row.vendorName || "").toLowerCase()
  );
  const leaseVendor = vendors.find(
    (v) =>
      String(v.vendorName || "").toLowerCase() ===
      String(row.leaseVendorName || "").toLowerCase()
  );
  const rentVendor = vendors.find(
    (v) =>
      String(v.vendorName || "").toLowerCase() ===
      String(row.rentVendorName || "").toLowerCase()
  );
  const dept = departments.find(
    (d) =>
      String(d.departmentName || d.name || "").toLowerCase() ===
      String(row.assignedDepartmentName || "").toLowerCase()
  );
  const assignedUser = users.find(
    (u) =>
      String(u.email || "").toLowerCase() ===
      String(row.assignedToEmail || "").toLowerCase()
  );
  const ownerUser = users.find(
    (u) =>
      String(u.email || "").toLowerCase() ===
      String(row.ownerEmail || "").toLowerCase()
  );
  const customer = customers.find(
    (c) =>
      String(c.customerId || c.id || "").toLowerCase() ===
      String(row.customerId || "").toLowerCase()
  );

  const ownershipCfg = getOwnershipTypeConfig(row.ownershipType);
  const assetCfg = getAssetTypeConfig(row.assetType);
  const showsFinancial = ownershipCfg.showsFinancialSection;
  const showsLease = ownershipCfg.showsLeaseFields;
  const showsRental = ownershipCfg.showsRentalFields;
  const showsDepr = ownershipCfg.showsDepreciation;

  const ownLower = String(row.ownershipType || "").toLowerCase();
  const isReturnOnly = ownLower === "rented" || ownLower === "leased";
  const serialPolicy = isReturnOnly
    ? "required"
    : resolveEffectiveSerialPolicy(row.assetType, subCat);
  const isSerialized =
    serialPolicy === "required"
      ? true
      : serialPolicy === "disabled"
        ? false
        : String(row.serializationType).toLowerCase() === "serialized";
  const showQty =
    assetCfg.tracksQuantity || (serialPolicy === "optional" && !isSerialized);

  const methodUpper = String(row.depreciationMethod || "").toUpperCase();
  const isWDV =
    methodUpper === "WDV" || methodUpper === "WRITTEN-DOWN-VALUE";
  const isSLM =
    methodUpper === "SLM" || methodUpper === "STRAIGHT-LINE";

  const purchaseValueNumber = toNum(row.purchaseValue);
  const residualValueNumber = isWDV ? 0 : toNum(row.residualValue);
  const openingBookValueNumber = isWDV
    ? toNum(row.openingBookValue || row.purchaseValue)
    : purchaseValueNumber;

  const now = new Date().toISOString();

  const payload = {
    id: targetDocId,
    assetId: aId,
    assetName: row.assetName,
    assetDescription: row.assetDescription || "",
    companyId: currentUser.companyId,

    assetType: row.assetType,
    ownershipType: row.ownershipType,

    categoryId: cat?.id || null,
    categoryCode: catCodeOf(cat) || row.categoryCode || null,
    categoryName: cat?.categoryName || cat?.name || null,
    subCategoryId: subCat?.id || null,
    subCategoryCode: subCodeOf(subCat) || row.subCategoryCode || null,
    subCategoryName: subCat?.subCategoryName || subCat?.name || null,
    brandId: brand?.id || null,
    brandName: brand?.name || brand?.brandName || row.brandName || null,

    locationCode: row.locationCode,
    status: row.status || "Available",

    serializationType:
      isReturnOnly || isSerialized ? "serialized" : "non-serialized",
    manufacturerSerialNumber:
      isSerialized || isReturnOnly
        ? row.manufacturerSerialNumber || null
        : null,
    quantity: showQty ? toNum(row.quantity) || 1 : 1,
    unitOfMeasurement: showQty ? row.unitOfMeasurement || null : null,

    purchaseDate: showsFinancial ? row.purchaseDate || null : null,
    purchaseValue: showsFinancial ? purchaseValueNumber : null,
    vendorId: showsFinancial ? vendor?.id || null : null,
    vendorName: showsFinancial
      ? vendor?.vendorName || row.vendorName || null
      : null,
    invoiceNumber: showsFinancial ? row.invoiceNumber || null : null,
    warrantyExpiry:
      String(row.assetType).toLowerCase() === "customer" || showsFinancial
        ? row.warrantyExpiry || null
        : null,

    salesDate:
      String(row.assetType).toLowerCase() === "customer"
        ? row.salesDate || null
        : null,
    salesValue:
      String(row.assetType).toLowerCase() === "customer"
        ? toNum(row.salesValue)
        : null,
    customerId:
      String(row.assetType).toLowerCase() === "customer"
        ? customer?.id || row.customerId || null
        : null,
    customerName:
      String(row.assetType).toLowerCase() === "customer"
        ? customer?.customerName || null
        : null,
    parentAssetId: row.__parentDocId || null,
    parentAssetName: row.__parentAssetName || null,
    purchasedSeparately: false,
    assignedTo: assignedUser?.id || null,
    assignedToName:
      assignedUser?.displayName || assignedUser?.name || null,
    assignedDepartmentId: dept?.id || null,
    assignedDepartmentName:
      dept?.departmentName || dept?.name || null,
    assignedOn: assignedUser ? now : null,

    ownerId: ownershipEnabled ? ownerUser?.id || null : null,
    ownerName: ownershipEnabled
      ? ownerUser?.displayName || ownerUser?.name || null
      : null,

      depreciationMethod: showsDepr ? row.depreciationMethod || null : null,
    usefulLife: showsDepr && isSLM ? parseInt(row.usefulLife, 10) : null,
    depreciationRate: showsDepr && isWDV ? toNum(row.depreciationRate) : null,
    residualValue: showsDepr ? residualValueNumber : null,
    openingBookValue: showsDepr ? openingBookValueNumber : null,
    isOpeningBookValueManual: showsDepr ? !isEmpty(row.openingBookValue) : false,
    isDepreciationAuto: true,
    maintenanceFrequency: row.maintenanceFrequency || null,
    maintenanceStartDate: row.maintenanceStartDate || null,

    qrToken,

    approvalStatus: "Draft", 
    approverList: (() => {
      if (!approverEnabled) return [];
      const svlApprovers = Array.isArray(masters.approvers)
        ? masters.approvers
        : [];
      if (svlApprovers.length === 0) return [];

      return svlApprovers
        .map((r) => {
          const level = Number(r.level) || 0;
          if (level <= 0) return null;
          const usersList = Array.isArray(r.users) ? r.users : [];
          const emails = Array.from(
            new Set(
              usersList
                .map((e) => String(e || "").trim().toLowerCase())
                .filter((e) => e.includes("@"))
            )
          );
          if (emails.length === 0) return null;
          return {
            level,
            label: r.label || `Level ${level}`,
            users: emails,
            approvedUsers: [],
            currentIndex: 0,
            actedBy: null,
            actedAt: null,
            status:
              level === 1 ? "Pending Level 1" : `Pending Level ${level}`,
            email: emails[0],
            name: null,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.level - b.level);
    })(),
    approvalHistory: [
      {
        level: 0,
        status: "Draft",
        createdBy: currentUser?.id || currentUser?.uid || null,
        createdByEmail: (currentUser?.email || "").toLowerCase(),
        createdAt: now,
      },
    ],
    approverEmails: [],
  };

   // Flatten approver emails for My Approvals queries
  if (approverEnabled && Array.isArray(payload.approverList)) {
    payload.approverEmails = Array.from(
      new Set(
        payload.approverList.flatMap((r) =>
          (r.users || []).map((e) => String(e).toLowerCase())
        )
      )
    );
    // Mirror CreateEditAssetModal: derive from the approver rows, not hardcoded.
    payload.approvalStatus = payload.approverList.length
      ? deriveApprovalStatus(payload.approverList)
      : "NA";
  } else {
    // Approval workflow disabled → same convention the modal uses.
    payload.approvalStatus = "NA";
  }
  if (showsLease) {
    Object.assign(payload, {
      leaseStartDate: row.leaseStartDate || null,
      leaseEndDate: row.leaseEndDate || null,
      leaseAmount: toNum(row.leaseAmount),
      leaseVendorId: leaseVendor?.id || null,
      leaseVendorName: row.leaseVendorName || "",
      leaseAgreementNumber: row.leaseAgreementNumber || "",
      leaseBillingFrequency: row.leaseBillingFrequency || "",
    });
  }

  if (showsRental) {
    Object.assign(payload, {
      rentStartDate: row.rentStartDate || null,
      expectedReturnDate: row.expectedReturnDate || null,
      rentAmount: toNum(row.rentAmount),
      rentVendorId: rentVendor?.id || null,
      rentVendorName: row.rentVendorName || "",
      rentAgreementNumber: row.rentAgreementNumber || "",
      rentBillingFrequency: row.rentBillingFrequency || "",
    });
  }

  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  return payload;
}

// ── 7. Template with dropdowns (Lists has headers) ───────────
async function generateTemplateWithDropdowns(currentUser) {
  const companyId = currentUser.companyId;
  const masters = await loadMasters(companyId);

  // Resolve the ownership preference so the template matches CreateEditAssetModal
  // (Asset Owner column only appears when ownership tracking is enabled).
  try {
    masters.ownershipEnabled = Boolean(
      await preferencesService.isOwnershipEnabled(currentUser)
    );
  } catch (_) {
    masters.ownershipEnabled = false;
  }

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Assets");
  const lists = wb.addWorksheet("Lists");

    // Assets headers
  TEMPLATE_HEADERS.forEach((h, i) => {
    const cell = ws.getCell(1, i + 1);
    cell.value = h;

    if (ALWAYS_REQUIRED_HEADERS.has(h)) {
      // Mandatory → red bold
      cell.font = { bold: true, color: { argb: "FFC00000" } };
    } else if (CONDITIONAL_HEADERS.has(h)) {
      // Conditionally required → amber italic
      cell.font = { bold: true, italic: true, color: { argb: "FFB45309" } };
    } else {
      cell.font = { bold: true };
    }

    cell.alignment = { vertical: "middle", wrapText: true };
  });
   // ── Collapsible column groups for Lease / Rent / Sales blocks ──
  // Columns are NEVER physically hidden — Excel cannot do that without VBA.
  // Instead, we group them into collapsible outlines and highlight them
  // based on Ownership Type / Asset Type via conditional formatting.
  const COLUMN_GROUPS = {
    lease: [
      "Lease Start Date",
      "Lease End Date",
      "Lease Amount",
      "Lease Vendor Name",
      "Lease Agreement Number",
      "Lease Billing Frequency",
    ],
    rent: [
      "Rent Start Date",
      "Expected Return Date",
      "Rent Amount",
      "Rent Vendor Name",
      "Rent Agreement Number",
      "Rent Billing Frequency",
    ],
    sales: ["Sales Date", "Sales Value", "Customer ID"],
  };

  const MAX_ROW = 500;
  const ownershipColLetter = colLetter(
    TEMPLATE_HEADERS.indexOf("Ownership Type") + 1
  );
  const assetTypeColLetter = colLetter(
    TEMPLATE_HEADERS.indexOf("Asset Type") + 1
  );

  // 1) Collapsible outlines
  Object.values(COLUMN_GROUPS).forEach((headers) => {
    headers.forEach((h) => {
      const idx = TEMPLATE_HEADERS.indexOf(h) + 1;
      if (idx < 1) return;
      ws.getColumn(idx).outlineLevel = 1;
    });
  });

  ws.properties.outlineProperties = {
    summaryBelow: false,
    summaryRight: false,
  };

  // 2) Highlight the ACTIVE block per row based on Ownership Type / Asset Type
  const applyBlockHighlight = (headers, isActiveFormula) => {
    headers.forEach((h) => {
      const idx = TEMPLATE_HEADERS.indexOf(h) + 1;
      if (idx < 1) return;
      const letter = colLetter(idx);

      // Header row (row 1) stays bold-neutral
      // Data rows 2..MAX_ROW get the conditional format
      ws.addConditionalFormatting({
        ref: `${letter}2:${letter}${MAX_ROW}`,
        rules: [
          {
            type: "expression",
            formulae: [isActiveFormula],
            style: {
              fill: {
                type: "pattern",
                pattern: "solid",
                bgColor: { argb: "FFEFF6FF" },
              },
              font: { bold: true, color: { argb: "FF1E40AF" } },
            },
          },
        ],
      });
    });
  };

  applyBlockHighlight(
    COLUMN_GROUPS.lease,
    `LOWER($${ownershipColLetter}2)="leased"`
  );
  applyBlockHighlight(
    COLUMN_GROUPS.rent,
    `LOWER($${ownershipColLetter}2)="rented"`
  );
  applyBlockHighlight(
    COLUMN_GROUPS.sales,
    `LOWER($${assetTypeColLetter}2)="customer"`
  );
    // ── Build the Lists sheet purely from DROPDOWN_SOURCES ──
  // Nothing is hardcoded: header names, list keys, and value mappers all
  // come from src/utils/assetImportTemplate.js (same contract the modal uses).
  const listEntries = Object.entries(DROPDOWN_SOURCES).filter(([, src]) => {
    if (src.requiresOwnership && !masters.ownershipEnabled) return false;
    return true;
  });

  // Row 1 = list headers
  listEntries.forEach(([headerName], i) => {
    const cell = lists.getCell(1, i + 1);
    cell.value = headerName;
    cell.font = { bold: true };
  });

  const resolveListValues = ({ listKey, valueMapper }) => {
    if (listKey === "serializationTypes") {
      return ["serialized", "non-serialized"];
    }
    const raw = masters[listKey] || [];
    return raw
      .map((e) => valueMapper(e))
      .filter((v) => v != null && String(v).trim() !== "")
      .map((v) => String(v).trim());
  };

  // Data rows start at row 2
  listEntries.forEach(([, src], colIdx) => {
    const values = resolveListValues(src);
    values.forEach((v, r) => {
      lists.getCell(r + 2, colIdx + 1).value = v;
    });
  });

   
  listEntries.forEach(([headerName], listIdx) => {
    const assetColIdx = TEMPLATE_HEADERS.indexOf(headerName) + 1;
    if (assetColIdx < 1) return;

    const assetLetter = colLetter(assetColIdx);
    const listLetter = colLetter(listIdx + 1);

    let listCount = 0;
    while (lists.getCell(listCount + 2, listIdx + 1).value) listCount++;
    if (listCount < 1) return; // no values → skip dropdown entirely

    for (let r = 2; r <= MAX_ROW; r++) {
      ws.getCell(`${assetLetter}${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`Lists!$${listLetter}$2:$${listLetter}$${listCount + 1}`],
        showErrorMessage: true,
        errorTitle: "Invalid value",
        error: "Select a value from the dropdown list only.",
      };
    }
  });
  // Hide Lists — users only see Assets + Instructions
  lists.state = "hidden";

  // Instructions
  const info = wb.addWorksheet("Instructions");
  info.getCell(1, 1).value = "Niyantrix Asset Import Template";
  info.getCell(1, 1).font = { bold: true, size: 14 };
  info.getCell(3, 1).value =
    "1. Use only the Assets sheet. Do not change header names or column order.";
  info.getCell(4, 1).value =
    "2. Asset ID is generated automatically (same as Create Asset on screen).";
    info.getCell(5, 1).value =
    "3. Purchase Date is required for EVERY asset, regardless of ownership type.";
  info.getCell(6, 1).value =
    "4. Owned: also fill Purchase Value + Vendor Name; leave Lease and Rent columns blank.";
  info.getCell(7, 1).value =
    "5. Leased: also fill Lease columns; leave Rent and Sales blank. Rented: fill Rent columns; leave Lease and Sales blank.";
  info.getCell(8, 1).value =
    "6. Customer asset type: fill Sales Date, Sales Value, Customer ID.";
  info.getCell(9, 1).value =
    "7. Dates must be YYYY-MM-DD. Use dropdowns for master data where available.";
  info.getCell(10, 1).value =
    "8. If any row fails validation, the entire file is rejected. Fix rows and re-upload.";
  info.getCell(11, 1).value =
    "9. The Lists sheet is hidden — it only supplies dropdown values.";
  info.getCell(12, 1).value =
    "10. Lease / Rent / Sales columns are collapsible groups. Expand only the block that matches your Ownership Type (Leased / Rented) or Asset Type (Customer). The relevant block is highlighted in blue automatically.";
   info.getCell(13, 1).value =
    "11. If you fill a Lease column for an Owned asset (or Rent for Leased, etc.), the row will be rejected. Leave irrelevant blocks blank.";

  // ── Add the Color Legend here ──
  info.getCell(15, 1).value = "Header Color Legend:";
  info.getCell(15, 1).font = { bold: true, size: 12 };

  info.getCell(16, 1).value = "Red Text:";
  info.getCell(16, 1).font = { bold: true, color: { argb: "FFC00000" } };
  info.getCell(16, 2).value = "= Always required on EVERY row, regardless of Asset Type or Ownership.";

  info.getCell(17, 1).value = "Orange Text:";
  info.getCell(17, 1).font = { bold: true, italic: true, color: { argb: "FFB45309" } };
  info.getCell(17, 2).value = "= Conditionally required. Only fill this if it matches your Ownership Type (e.g., Leased) or Asset Type (e.g., Customer). Filling it incorrectly will cause an error.";
  // ────────────────────────────────

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

// ── 8. Public API ────────────────────────────────────────────
export const assetImportService = {
  async importFromFile(file, currentUser) {
    if (!currentUser?.companyId) {
      throw new Error("Company context required.");
    }
   
    try {
      const existingAssets = await assetService.fetchAssets({
        companyId: currentUser.companyId,
      });
      if (existingAssets && existingAssets.length > 0) {
        return {
          success: false,
          stage: "guard",
          message:
            "Import is only available before any asset exists. Existing assets were found — use Create Asset or the UI instead.",
          errors: [],
        };
      }
    } catch (guardErr) {
      return {
        success: false,
        stage: "guard",
        message: `Could not verify asset count: ${guardErr.message}`,
        errors: [],
      };
    }
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    // Prefer sheet named Assets
    const worksheet =
      workbook.Sheets["Assets"] || workbook.Sheets[workbook.SheetNames[0]];

    if (!worksheet) {
      return {
        success: false,
        stage: "parse",
        message: "No worksheet found in the file.",
        errors: [],
      };
    }

    // 1. Authenticity
    const auth = checkTemplateAuthenticity(worksheet);
    if (!auth.ok) {
      return {
        success: false,
        stage: "authenticity",
        message: auth.message,
        errors: [],
      };
    }

    // 2. Parse
    const parsed = parseRows(worksheet);
    if (!parsed.ok) {
      return {
        success: false,
        stage: "parse",
        message: parsed.message,
        errors: [],
      };
    }

    // 3. Masters
    const masters = await loadMasters(currentUser.companyId);

     let ownershipEnabled = false;
    let approverEnabled = false;
    try {
      ownershipEnabled = await preferencesService.isOwnershipEnabled(
        currentUser
      );
    } catch (_) {
      /* default false */
    }
    try {
      approverEnabled = await preferencesService.isApproverEnabled(
        currentUser
      );
    } catch (_) {
      /* default false */
    }

    // 4. Validate ALL rows — no writes
    const errors = await validateAllRows(
      parsed.rows,
      currentUser.companyId,
      masters
    );
    if (errors.length > 0) {
      return {
        success: false,
        stage: "validation",
        message: `${errors.length} row(s) failed validation. Nothing was imported.`,
        errors,
      };
    }

       // 5. Asset ID preference — mirror CreateEditAssetModal 1:1.
    //    The modal parses assetPref.preferenceValue, bumps the one key,
    //    mutates assetPref.preferenceValue, and calls preferencesService.upsert.
    //    We do the exact same thing. Do NOT rebuild the doc and do NOT use
    //    repo.upsertData — that resets every other field.
    const assetPref = await preferencesService.getByPreferenceId(
      currentUser,
      "assets-id"
    );

    // Tolerant parse: string (canonical) OR object (some repo layers).
    // On failure we return {} but we DO NOT lose the doc because we will
    // rewrite it via preferencesService.upsert(assetPref.id, assetPref).
    const parsePrefValue = (raw) => {
      if (raw == null) return {};
      if (typeof raw === "string") {
        try {
          const p = JSON.parse(raw);
          return p && typeof p === "object" && !Array.isArray(p) ? p : {};
        } catch (e) {
          console.warn("[AssetImport] preferenceValue parse failed:", e, raw);
          return {};
        }
      }
      if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
      return {};
    };

    // Working copy — never mutate assetPref.preferenceValue directly until
    // the final write, so a mid-import rollback leaves the doc untouched.
    const prefWorking = parsePrefValue(assetPref?.preferenceValue);

    // Same company-code resolution as CreateEditAssetModal
    const companyCodeForId =
      assetPref?.companyId || currentUser.companyId;

    // 6. Insert all-or-nothing
    const createdIds = [];
    try {
      for (const row of parsed.rows) {
           const categoryCode = row.categoryCode;

        // Live counter read — preserves the value from previous imports.
        // If the same category appears N times in this one file, each row
        // gets the next number (2299, 2300, …). Different categories are
        // untouched because prefWorking started as a copy of the full map.
        const currentSeq = prefWorking[categoryCode];
        const nextSeq =
          Number.isFinite(currentSeq) && currentSeq > 0 ? currentSeq : 1;

        const targetDocId = generateUUID();
        const aId =
          companyCodeForId +
          "-" +
          categoryCode +
          "-" +
          String(nextSeq).padStart(5, "0");
        const qrToken = await generateQRToken(
          targetDocId,
          currentUser.userId || currentUser.id,
          currentUser.companyId
        );

              const payload = buildPayload(
          row,
          masters,
          currentUser,
          targetDocId,
          aId,
          qrToken,
          ownershipEnabled,
          approverEnabled
        );
        await assetService.upsertData(targetDocId, payload, {
          isCreation: true,
        });
        createdIds.push(targetDocId);

        prefWorking[categoryCode] = nextSeq + 1;
      }

           // Preference write — mirror CreateEditAssetModal 1:1.
      //
      // If the doc already exists, we mutate assetPref.preferenceValue and
      // write it back through preferencesService.upsert(assetPref.id, assetPref).
      // This preserves: scope, userId, createdAt, createdBy, deleted, and any
      // custom fields the doc may carry. Only `preferenceValue` is updated.
      //
      // If the doc does NOT exist (fresh company), we create it once with the
      // exact schema the modal uses, then future writes go through the
      // existing-doc branch above.
      const newPrefValueString = JSON.stringify(prefWorking);
      const nowIso = new Date().toISOString();

      console.info(
        "[AssetImport] assets-id preference merge:",
        {
          before: parsePrefValue(assetPref?.preferenceValue),
          after: prefWorking,
          addedByThisImport: parsed.rows.reduce((acc, r) => {
            acc[r.categoryCode] = (acc[r.categoryCode] || 0) + 1;
            return acc;
          }, {}),
        }
      );

      if (assetPref && assetPref.id) {
        // Existing preference — update in place, same as the modal.
        const updatedPref = {
          ...assetPref,
          preferenceValue: newPrefValueString,
          updatedAt: nowIso,
          updatedBy: currentUser?.email || "system",
          updated_at: nowIso,
        };
        await preferencesService.upsert(
          currentUser,
          updatedPref.id,
          updatedPref
        );
      } else {
        // Fresh company — create with the exact schema the modal uses.
        const prefDocId = `${currentUser.companyId}AssetPreference`;
        const freshPref = {
          companyId: currentUser.companyId,
          deleted: false,
          id: prefDocId,
          preferenceId: "assets-id",
          preferenceName: "assets_id",
          preferenceType: "__system__",
          preferenceValue: newPrefValueString,
          scope: null,
          createdAt: nowIso,
          createdBy: currentUser?.email || "system",
          updatedAt: nowIso,
          updatedBy: currentUser?.email || "system",
          updated_at: nowIso,
          userId: null,
        };
        await preferencesService.upsert(
          currentUser,
          freshPref.id,
          freshPref
        );
      }

      return {
        success: true,
        message: `Successfully imported ${createdIds.length} asset(s).`,
        count: createdIds.length,
      };
           } catch (err) {
      // Rollback: soft-delete created assets so a failed import leaves no
      // visible trace (fetchAssets filters out deletedFlag records).
         for (const id of createdIds) {
        try {
          if (typeof assetService.hardDeleteAsset === "function") {
            await assetService.hardDeleteAsset(id);
          } else {
            await assetService.deleteAsset(id);
          }
        } catch (_) {
         
        }
      }
      return {
        success: false,
        stage: "insert",
        message: `Import failed during insert: ${err.message}. All changes were rolled back.`,
        errors: [],
      };
    }
  },

   async generateTemplateBlob(currentUser) {
    if (!currentUser?.companyId) {
      throw new Error("Company required to build template dropdowns.");
    }
    return generateTemplateWithDropdowns(currentUser);
  },

  async exportAsImportTemplate(currentUser) {
    if (!currentUser?.companyId) {
      throw new Error("Company required to export.");
    }
    const [masters, assets] = await Promise.all([
      loadMasters(currentUser.companyId),
      assetService.fetchAssets({ companyId: currentUser.companyId }),
    ]);
    return exportRowsAsTemplate(assets || [], masters);
  },
};
// ── 9. Export as import-template (round-trippable) ───────────
function exportRowsAsTemplate(assets, masters) {
  const rows = assets.map((a) => {
    const cat = masters.categories.find((c) => c.id === a.categoryId);
    const sub = masters.subCategories.find((s) => s.id === a.subCategoryId);
    const brand = masters.brands.find((b) => b.id === a.brandId);
    const vendor = masters.vendors.find((v) => v.id === a.vendorId);
    const leaseVendor = masters.vendors.find((v) => v.id === a.leaseVendorId);
    const rentVendor = masters.vendors.find((v) => v.id === a.rentVendorId);
    const dept = masters.departments.find((d) => d.id === a.assignedDepartmentId);
    const assignedUser = masters.users.find((u) => u.id === a.assignedTo);
    const ownerUser = masters.users.find((u) => u.id === a.ownerId);
    const customer = masters.customers.find((c) => c.id === a.customerId);

    const map = {
      assetName: a.assetName || "",
      assetDescription: a.assetDescription || "",
      assetType: a.assetType || "",
      ownershipType: a.ownershipType || "",
      parentAssetId: a.parentAssetId || "",
      categoryCode: catCodeOf(cat) || a.categoryCode || "",
      subCategoryCode: subCodeOf(sub) || a.subCategoryCode || "",
      brandName: brand?.name || brand?.brandName || a.brandName || "",
      locationCode: a.locationCode || "",
      status: a.status || "",
      serializationType: a.serializationType || "",
      manufacturerSerialNumber: a.manufacturerSerialNumber || "",
      quantity: a.quantity ?? "",
      unitOfMeasurement: a.unitOfMeasurement || "",
      purchaseDate: a.purchaseDate || "",
      purchaseValue: a.purchaseValue ?? "",
      vendorName: vendor?.vendorName || a.vendorName || "",
      invoiceNumber: a.invoiceNumber || "",
      warrantyExpiry: a.warrantyExpiry || "",
      leaseStartDate: a.leaseStartDate || "",
      leaseEndDate: a.leaseEndDate || "",
      leaseAmount: a.leaseAmount ?? "",
      leaseVendorName: leaseVendor?.vendorName || a.leaseVendorName || "",
      leaseAgreementNumber: a.leaseAgreementNumber || "",
      leaseBillingFrequency: a.leaseBillingFrequency || "",
      rentStartDate: a.rentStartDate || "",
      expectedReturnDate: a.expectedReturnDate || "",
      rentAmount: a.rentAmount ?? "",
      rentVendorName: rentVendor?.vendorName || a.rentVendorName || "",
      rentAgreementNumber: a.rentAgreementNumber || "",
      rentBillingFrequency: a.rentBillingFrequency || "",
      salesDate: a.salesDate || "",
      salesValue: a.salesValue ?? "",
      customerId: customer?.customerId || customer?.id || a.customerId || "",
      depreciationMethod: a.depreciationMethod || "",
      usefulLife: a.usefulLife ?? "",
      depreciationRate: a.depreciationRate ?? "",
      residualValue: a.residualValue ?? "",
      openingBookValue: a.openingBookValue ?? "",
      maintenanceFrequency: a.maintenanceFrequency || "",
      maintenanceStartDate: a.maintenanceStartDate || "",
      assignedDepartmentName: dept?.departmentName || dept?.name || "",
      assignedToEmail: assignedUser?.email || "",
      ownerEmail: ownerUser?.email || "",
    };

    // Emit in exact TEMPLATE_HEADERS order
    return TEMPLATE_HEADERS.map((h) => {
      const f = HEADER_TO_FIELD[h];
      return map[f] ?? "";
    });
  });

  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Assets");
  const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}
export default assetImportService;